// ABOUTME: Reusable date chip picker — "+" button toggles an inline calendar for selecting dates.
// ABOUTME: Selected dates render as closable Pill chips grouped into Upcoming and Past sections.
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import ReactDatepicker from 'react-datepicker'
import {Button, Pill} from '@hm/fundamental'
import {mdiArrowLeft, mdiArrowRight, mdiPlus} from '@mdi/js'

import {useTranslation} from '../../../../hooks/useTranslation'
import './DateChipPicker.css'

interface DateChipPickerProps {
  /** ISO date strings (YYYY-MM-DD) */
  value: string[]
  /** Initial dates from the server — used to determine which dates are "new" */
  initialValue?: string[]
  /** Called with updated sorted array when dates are added/removed */
  onChange: (dates: string[]) => void
  /** Disables the "+" button and chip close buttons */
  disabled?: boolean
  /** Test ID prefix */
  'data-cy'?: string
  /** Validation error message to display below the picker */
  error?: string
}

/** Format ISO date string to DD/Mon/YYYY (e.g., "25/Dec/2026") */
const formatDateChip = (iso: string): string => {
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  const dd = String(date.getDate()).padStart(2, '0')
  const mon = date.toLocaleString('en', {month: 'short'})
  const yyyy = date.getFullYear()
  return `${dd}/${mon}/${yyyy}`
}

/** Convert Date object to ISO string YYYY-MM-DD in local time */
const toIsoDate = (date: Date): string => {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const getTodayIso = (): string => toIsoDate(new Date())

const CalendarHeader = ({
  date,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: {
  date: Date
  decreaseMonth: () => void
  increaseMonth: () => void
  prevMonthButtonDisabled: boolean
  nextMonthButtonDisabled: boolean
}) => {
  const monthYear = date.toLocaleString('en', {month: 'long', year: 'numeric'})
  return (
    <div className="flex justify-between items-center bg-inherit h-12">
      <Button variant="tertiary" size="small" type="button" icon={mdiArrowLeft} square onClick={decreaseMonth} disabled={prevMonthButtonDisabled} />
      <span className="text-lg font-semibold">{monthYear}</span>
      <Button variant="tertiary" size="small" type="button" icon={mdiArrowRight} square onClick={increaseMonth} disabled={nextMonthButtonDisabled} />
    </div>
  )
}

export const DateChipPicker: React.FC<DateChipPickerProps> = ({
  value,
  initialValue = [],
  onChange,
  disabled,
  'data-cy': dataCy,
  error,
}) => {
  const {t} = useTranslation()
  const [calendarOpen, setCalendarOpen] = useState(false)
  const calendarRef = useRef<HTMLDivElement>(null)

  const today = getTodayIso()
  const upcomingDates = useMemo(() => value.filter((iso) => iso >= today), [value, today])
  const pastDates = useMemo(() => value.filter((iso) => iso < today), [value, today])

  useEffect(() => {
    if (!calendarOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setCalendarOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [calendarOpen])

  const handleDateSelected = useCallback(
    (date: Date | [Date | null, Date | null] | null) => {
      if (!date || Array.isArray(date)) return
      const iso = toIsoDate(date)
      if (value.includes(iso)) {
        onChange(value.filter((d) => d !== iso))
      } else {
        onChange([...value, iso].sort())
      }
    },
    [value, onChange],
  )

  const handleRemove = useCallback(
    (iso: string) => {
      onChange(value.filter((d) => d !== iso))
    },
    [value, onChange],
  )

  return (
    <div className="flex flex-col gap-1">
      <div data-cy={dataCy} className="flex flex-col gap-6">
      {/* Upcoming section */}
      <div className="flex flex-col gap-4" data-cy={`${dataCy}-upcoming`}>
        <h5 className="text-base font-semibold">
          {t('countryConfigurations.operationalHours.nonWorkingDays.upcoming')}
        </h5>
        <div className="flex flex-wrap gap-2 items-center">
          <div ref={calendarRef} className="relative inline-flex">
            <Button
              variant="secondary"
              size="small"
              type="button"
              icon={mdiPlus}
              square
              disabled={disabled}
              onClick={() => setCalendarOpen((prev) => !prev)}
              aria-label={t('countryConfigurations.operationalHours.nonWorkingDays.addDate')}
              data-cy={`${dataCy}-add`}
            />

            {/* Calendar overlay — positioned above the button */}
            {calendarOpen && (
              <div
                className="fundamental-datepicker date-chip-calendar absolute bottom-full left-0 mb-2 z-50 isolate"
                data-cy={`${dataCy}-calendar`}
              >
                <ReactDatepicker
                  inline
                  selected={null}
                  onChange={handleDateSelected}
                  minDate={new Date()}
                  calendarClassName="!border-0 !bg-[var(--container_background)] shadow-md rounded p-[10px]"
                  calendarStartDay={1}
                  renderCustomHeader={(props) => <CalendarHeader {...props} />}
                  formatWeekDay={(day) => day.substring(0, 3)}
                  dayClassName={(date) => {
                    const cls = 'w-[46px] h-[46px] text-base'
                    if (upcomingDates.includes(toIsoDate(date))) return `${cls} day-selected`
                    if (date.getDay() % 6 === 0) return `${cls} day-weekend`
                    return cls
                  }}
                  weekDayClassName={() => 'w-[46px]'}
                  disabled={disabled}
                />
              </div>
            )}
          </div>

          {upcomingDates.map((iso) => (
            <Pill
              key={iso}
              type="info"
              variant="shade"
              size="regular"
              text={initialValue.includes(iso) ? formatDateChip(iso) : `${formatDateChip(iso)} ${t('countryConfigurations.operationalHours.nonWorkingDays.newTag')}`}
              closable={!disabled}
              onClose={() => handleRemove(iso)}
              data-cy={`${dataCy}-chip-${iso}`}
            />
          ))}
        </div>
      </div>

      {/* Past section — only rendered if there are past dates */}
      {pastDates.length > 0 && (
        <div className="flex flex-col gap-4" data-cy={`${dataCy}-past`}>
          <h5 className="text-base font-semibold">
            {t('countryConfigurations.operationalHours.nonWorkingDays.past')}
          </h5>
          <div className="flex flex-wrap gap-2 items-start">
            {pastDates.map((iso) => (
              <Pill
                key={iso}
                type="neutral"
                variant="shade"
                size="regular"
                text={formatDateChip(iso)}
                data-cy={`${dataCy}-chip-${iso}`}
              />
            ))}
          </div>
        </div>
      )}
      </div>
      {error && <p className="text-xs leading-4 text-negative">{error}</p>}
    </div>
  )
}
